import { useProfile } from "@/contexts/ProfileProvider";
import { useTheme } from "@/contexts/ThemeProvider";
import { IContactForm } from "@/service/types";
import { useSendContactMutation } from "@/service/user/hooks";
import { useToast, Grid, Box, VStack, Image } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { HiOutlineMailOpen } from "react-icons/hi";
import z from "zod";
import { SelectInput, InputArea, Input } from "../atoms/Input";
import { Text } from "../atoms/Text";
import { validarCPF } from "@/utils/validateDocuments";
import { Button } from "../atoms/Button";


export function ContactForm() {
  const [isEmailSended, setIsEmailSended] = React.useState(false);
  const toast = useToast();
  const { profile } = useProfile();

  const { name: agreementName, colors } = useTheme();

  const formSchema = z.object({
    name: z.string().min(1, { message: 'Nome é obrigatório' }),
    cellphone: z.string().min(15, { message: 'Celular deve ter pelo menos 15 caracteres' }),
    email: z.string().email({ message: 'Email inválido' }),
    cpf: z.string().min(14, { message: 'CPF deve ter pelo menos 14 caracteres' }),
    subject: z.string().min(1, { message: 'Assunto é obrigatório' }),
    message: z.string().min(10, { message: 'Mensagem deve ter pelo menos 10 caracteres' }),
  });

  const methods = useForm<IContactForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: profile?.fullName || '',
      email: profile?.email || '',
      cellphone: profile?.telephone || '',
      subject: '',
      message: '',
    },
  });

  const { control, handleSubmit, reset } = methods;

  const handleSubmitEmail = (data: IContactForm) => {
    if (!validarCPF(data.cpf)) {
      toast({
        status: 'error',
        title: 'Opss!',
        description: 'O CPF informado é inválido',
      });
      return;
    }

    const payload: IContactForm = {
      name: data.name,
      email: data.email,
      subject: data.subject,
      message: data.message,
      cpf: data.cpf,
      cellphone: data.cellphone,
      agreement: agreementName,
      landing: 'Landing ' + agreementName,
      origin: 'landing',
    };

    mutation.mutate(payload);
  };

  const mutation = useSendContactMutation({
    onSuccess() {
      toast({
        title: 'Mensagem Enviada!',
        description: 'Mensage enviada com sucesso. Logo um de nossos funcionários estará entrando em contato com você.',
        status: 'success',
      });
      reset();
      setIsEmailSended(true);
    },
    onError() {
      toast({
        title: 'Mensagem Enviada!',
        description: 'Mensage enviada com sucesso. Logo um de nossos funcionários estará entrando em contato com você.',
        status: 'success',
      });
      reset();
      setIsEmailSended(true);
    },
  });

  return (
    <Grid width={'100%'} templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}>
      <Box
        display={{
          base: 'none',
          md: 'flex',
        }}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Image padding="20px" width={'70%'} marginRight="auto" src="/assets/side-image.png" />
      </Box>
      {!isEmailSended ? (
        <form style={{ width: '100%' }} onSubmit={handleSubmit(handleSubmitEmail)}>
          <VStack w={'100%'} spacing={7}>
            <Text fontSize={'14px'}>
              Tem alguma dúvida? Preencha o formulário abaixo que entraremos em contato com você.
            </Text>
            <Input control={control} name="name" label="Nome *" />
            <Input control={control} mask="cell" name="cellphone" label="Celular *" />
            <Input
              control={control}
              name="email"
              label="Email *"
              rightElement={<HiOutlineMailOpen color="#292927" />}
            />
            <Input control={control} mask="cpf" name="cpf" label="CPF *" />
            <SelectInput
              options={[
                { value: 'Dúvidas', label: 'Dúvidas' },
                { value: 'Quero cadastrar meu estabelecimento', label: 'Quero cadastrar meu estabelecimento' },
                {
                  value: 'Quero fornecer a plataforma para meus clientes/funcionários/associados',
                  label: 'Quero fornecer a plataforma para meus clientes/funcionários/associados',
                },
                { value: 'Reclamação', label: 'Reclamação' },
                { value: 'Exclusão', label: 'Exclusão' },
                { value: 'Outros', label: 'Outros' },
              ]}
              placeholder="Escolha um assunto"
              control={control}
              name="subject"
              label="Assunto *"
              autoWidth={false}
              multiple={false}
              native={false}
            />

            <InputArea control={control} name="message" label="Mensagem *" />

            <Button
              width={'fit-content'}
              padding="0px 50px"
              marginLeft={'auto'}
              isLoading={mutation.isLoading}
              type="submit"
            >
              Enviar
            </Button>
          </VStack>
        </form>
      ) : (
        <VStack w={'100%'} spacing={7} align="center" justify="center">
          <Text textAlign="center" fontSize="20px" fontWeight="bold" color={colors.primaryColor}>
            Mensagem enviada com sucesso!
          </Text>
          <Text textAlign="center" fontSize="16px">
            Logo um de nossos funcionários estará entrando em contato com você.
          </Text>
          <Button mt={4} onClick={() => setIsEmailSended(false)} variant="outline" colorScheme="green">
            Enviar nova mensagem
          </Button>
        </VStack>
      )}
    </Grid>
  );
}
